import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { AuthContextType } from '../types';
import { getFullPath } from '../utils/pathUtils';
import { forgetSignedInUser, rememberTabUser, tabUser } from './signedInState';
import {
  isNoSession,
  SESSION_ENDED_ELSEWHERE,
  SESSION_ENDED_HERE,
  SessionTrouble,
  sessionTrouble as troubleOf,
  wasSessionEnded,
} from './sessionErrors';
import { GET_USER_PROFILE } from '../graphql';
import { showInkToast } from '../components/cms/InkToast';

const LOGOUT = gql`
  mutation Logout {
    logout
  }
`;

/** Seconds between tries when the check gets no answer; the last one repeats. */
const RETRY_SECONDS = [2, 4, 8, 15];

/** The cookie now belongs to someone else: another tab signed in here. */
function noteTabUser(me: { id: string; email?: string | null }) {
  const before = tabUser();
  if (before && before !== me.id) {
    showInkToast(`Another tab signed in as ${me.email}. This browser now uses that account.`);
  }
  rememberTabUser(me.id);
}

/** Why this tab's session is gone, for the sign-in page; a first visit says nothing. */
function leaveSignInReason(err: unknown) {
  const reason = wasSessionEnded(err) ? SESSION_ENDED_ELSEWHERE : tabUser() && SESSION_ENDED_HERE;
  rememberTabUser(null);
  if (reason) sessionStorage.setItem('authError', reason);
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTrouble, setSessionTrouble] = useState<SessionTrouble | null>(null);
  const client = useApolloClient();
  const stopChecking = useRef<() => void>(() => undefined);

  // Stays loading until the server says who this is: a failed check is not
  // "signed out", so it retries rather than sending anyone to sign in.
  useEffect(() => {
    const leftover = localStorage.getItem('token');
    if (leftover) localStorage.removeItem('token');
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (signedIn: boolean) => {
      setIsAuthenticated(signedIn);
      setSessionTrouble(null);
      setIsLoading(false);
    };
    const check = (attempt: number) => {
      client
        .query({
          query: GET_USER_PROFILE,
          fetchPolicy: 'network-only',
          context: leftover ? { headers: { authorization: `Bearer ${leftover}` } } : {},
        })
        .then(({ data }) => {
          if (stopped) return;
          if (data?.me?.id) noteTabUser(data.me);
          settle(Boolean(data?.me?.id));
        })
        .catch((err) => {
          if (stopped) return;
          if (isNoSession(err)) {
            leaveSignInReason(err);
            settle(false);
            return;
          }
          setSessionTrouble(troubleOf(err));
          const wait = RETRY_SECONDS[Math.min(attempt, RETRY_SECONDS.length - 1)];
          timer = setTimeout(() => check(attempt + 1), wait * 1000);
        });
    };
    stopChecking.current = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    check(0);
    return () => stopChecking.current();
  }, [client]);

  const login = () => {
    stopChecking.current();
    // Whoever this is, the next load starts fresh: no notice meant for the last session.
    rememberTabUser(null);
    sessionStorage.removeItem('authError');
    setSessionTrouble(null);
    setIsLoading(false);
    setIsAuthenticated(true);
  };

  /**
   * Sign out, then load the sign-in page afresh. A full page load every time:
   * flipping the auth flag first sends the router to /login inside this page,
   * and everything held in memory (the open site, query results) then carried
   * over to whoever signed in next — they saw the last person's site in the
   * header until a refresh (backlog a81cf711).
   */
  const logout = (message: string | null = null) => {
    const leave = () => {
      forgetSignedInUser();
      if (message) {
        sessionStorage.setItem('authError', message);
      }
      window.location.replace(getFullPath('/login'));
    };
    client
      .mutate({ mutation: LOGOUT })
      .catch(() => undefined)
      .then(() => client.clearStore())
      .then(leave, leave);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, sessionTrouble, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
